import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { Button, Col, Image, Row, Stack, InputGroup, FormControl, Alert } from 'react-bootstrap';
import CustomCheckbox from '../../components/common/CustomCheckbox';
import { FaCopy, FaInfoCircle } from "react-icons/fa";
import { toast } from 'react-toastify';
import sessionStorage from '../../helpers/sessionStorage';
import storage from '../../helpers/storage';
import { getDeviceID } from '../../helpers/utils';
import CustomInputText from '../../components/common/CustomInputText';
import CommonFormikComponent from '../../components/common/CommonFormikComponent';
import Loader from '../../components/common/loader';
import { ForgotSchema } from './validation'; // Reusing your code validation schema
import LOGIN_BANNER from "../../assets/images/login-banner.svg";
import LOGO_WHITE from '../../assets/images/logo-white.svg';
import { totpSetupAndLogin } from '../../services/auth.service';
import { redirectionRoutes } from '../../routes/redirectionRoutes';
import { useCustomStore } from '../../redux/store';

type FormValues = {
    email: string;
    code: string;
    rememberDevice: boolean;
    deviceID: string;
}

export default function TOTPSetup() {
    const AppName = process.env.REACT_APP_NAME;
    const navigate = useNavigate();
    const location = useLocation();
    const { setUser, setAuth0Login } = useCustomStore();
    const [loading, setLoading] = useState(false);
    const [copied, setCopied] = useState(false);
    const [isMounted, setIsMounted] = useState(false)
    const { email, auth0Login, totpData, isChildUser } = location.state;
    // Mock data - replace with API response from your setup call
    const qrCodeUrl = totpData?.qrCode || null;
    const secretKey = totpData?.base32 || null;

    
    const initialValues: FormValues = {
        email: email || "",
        code: "",
        rememberDevice: false,
        deviceID: email ? getDeviceID(email) : ''
    };

    /**
     * component mounted hook
     */
    useEffect(() => {
        // This code will run when the component is mounted
        setIsMounted(true);
        // Define cleanup logic here
        return () => {
            if (isMounted) {
                sessionStorage.delete('2fa');
            }
        };
    }, [isMounted]);

    /**
     * Check login state and email
     */
    useEffect(() => {
        const twoFAEmail = sessionStorage.getData("2fa");
        if (twoFAEmail !== email) {
            toast("Unauthorized page access.", { type: toast.TYPE.ERROR });
            navigate(redirectionRoutes.login.path);
        }
    }, [navigate, email])

    const handleCopySecret = () => {
        navigator.clipboard.writeText(secretKey);
        setCopied(true);
        toast.success("Secret key copied!");
        setTimeout(() => setCopied(false), 2000);
    };

    const handleSubmit = async (values: any, { setSubmitting }: any) => {
        setLoading(true);
        const payload = {
            tempSecret: secretKey,
            token: values.code,
            email: email,
            deviceID: values?.deviceID,
            rememberDevice: values?.rememberDevice
        }
        totpSetupAndLogin(payload).then((response: any) => {
            toast(response.message, { type: toast.TYPE.SUCCESS });
            setLoading(false);
            setSubmitting(false);
            sessionStorage.delete('2fa');
            if(auth0Login) {
                setAuth0Login(true);
            }
            storage.setToken(response.data.token.access.token);
            storage.setRefreshToken(response.data.token.refresh.token);
            setUser(response.data?.user);
            if(!response.data?.user?.profileVerification?.status) {
                navigate(redirectionRoutes.profile.path, { replace: true });
            } else {
                navigate(redirectionRoutes.dashboard.path, { replace: true });
            }
        }).catch((error: any) => {
            setLoading(false);
            setSubmitting(false);
            toast(error.data.message, { type: toast.TYPE.ERROR });
            //if user locked due to multiple attempt, navigate to the login
            if (error?.data?.messageCode === 3056) {
                navigate("/login")
            }
        })
    };

    return (
        <React.Fragment>
            <Loader isLoading={loading} />
            <Row className="g-0 vh-100">
                {/* Left Banner Section - Matches your design */}
                <Col md={6}>
                    <Stack className='custom-primary-gradient text-center justify-content-around h-100 p-4' gap={4}>
                        <div className='user-select-none'>
                            <Image fluid src={LOGO_WHITE} alt="Logo" width={240} />
                        </div>
                        <div>
                            <Image fluid src={LOGIN_BANNER} alt="Banner" width={389} />
                        </div>
                        <div className='lh-sm text-white'>
                            <div className='fw-light fs-4'>AI-Powered</div>
                            <div className='fw-semibold fs-1 text-uppercase'>Penetration Testing</div>
                            <div className='fw-light fs-4'>for Enterprises</div>
                        </div>
                    </Stack>
                </Col>

                {/* Setup Form Section */}
                <Col md={6} className="d-flex flex-column p-3 overflow-auto">
                    <div className="custom-width-320 d-flex flex-column flex-grow-1 mw-100 mx-auto py-3">
                        <div className='my-auto py-4'>
                            <p className="mb-1 opacity-75 text-uppercase">Security Setup</p>
                            <h1 className="fw-semibold mb-1 pb-1 fs-2">Setup TOTP MFA</h1>
                            {/* NEW: Admin Notification Section */}
                            {isChildUser && (
                                <Alert variant="info" className="d-flex align-items-start gap-2 fs-14 border-0 bg-light-blue mt-2 mb-4">
                                    <FaInfoCircle className="mt-1 flex-shrink-0" />
                                    <div>
                                        Your account administrator has enabled Two-Factor Authentication using the TOTP method to enhance your account security. Please follow the steps below to link your authenticator app and complete your login.
                                    </div>
                                </Alert>
                            )}
                            <p className='mb-4 sub-text'>
                                Scan the QR code with your authenticator app (Google Authenticator, Authy, etc.)
                            </p>

                            {/* QR Code Display */}
                            <div className="text-center mb-4 p-3 bg-light rounded">
                                <Image src={qrCodeUrl} alt="QR Code" className="img-fluid" style={{ maxWidth: '180px' }} />
                            </div>

                            {/* Secret Key Manual Entry */}
                            <div className="mb-4">
                                <label className="form-label fs-14 fw-medium">If you are unable to use the QR Code, you can enter this “Text Code” instead</label>
                                <InputGroup className="mb-3">
                                    <FormControl
                                        readOnly
                                        value={secretKey}
                                        className="bg-light fs-14 font-monospace"
                                    />
                                    <Button variant="outline-secondary" onClick={handleCopySecret}>
                                        <FaCopy size={18} />
                                    </Button>
                                </InputGroup>
                            </div>

                            {/* Verification Form */}
                            <CommonFormikComponent
                                validationSchema={ForgotSchema}
                                initialValues={initialValues}
                                onSubmit={handleSubmit}
                            >
                                {(formikProps) => (
                                    <>
                                        <CustomInputText
                                            wrapperClassname="mb-3"
                                            controlId="code"
                                            label='Verify Code'
                                            placeholder="Enter 6 digit code"
                                            touched={formikProps.touched.code}
                                            handleBlur={formikProps.handleBlur}
                                            handleChange={formikProps.handleChange}
                                            errorsField={formikProps.errors.code}
                                            value={formikProps.values.code}
                                        />
                                        <Row xs="auto" className="align-items-center justify-content-between gx-0 mb-3">
                                            <Col>
                                                <CustomCheckbox
                                                    controlId="rememberDevice"
                                                    value="Trust device for 14 days"
                                                    label='Trust device for 14 days'
                                                    checked={formikProps?.values.rememberDevice}
                                                    onChange={(e: any) => {
                                                        formikProps.setFieldValue('rememberDevice', e.target.checked);
                                                    }}
                                                />
                                            </Col>
                                        </Row>
                                        <Button 
                                            className="w-100" 
                                            variant="primary" 
                                            type="submit" 
                                            disabled={formikProps.isSubmitting || loading}
                                        >
                                            {loading ? 'Confirming...' : 'Verify and Login'}
                                        </Button>
                                    </>
                                )}
                            </CommonFormikComponent>
                            
                            <Stack direction='vertical' className="align-items-center mt-4">
                                <Link className="fs-14 fw-semibold text-decoration-none" to="/login">
                                    Back to Login
                                </Link>
                            </Stack>
                        </div>
                    </div>
                </Col>
            </Row>
        </React.Fragment>
    );
}